// Wallet balances for the swap card, read over public RPCs (no keys, nothing sent but the address
// and token): EVM chains each through their public endpoint (the wallet's own RPC is on whatever
// chain it is switched to), Solana and Jupnet through the demo's configured public RPCs, Sui
// through its public fullnode. A balance that cannot be read is null and simply not shown.

import type { Chain, ChainKey, Token } from "./chains";
import { NATIVE_SOL_MINT, isNativeEvm } from "./chains";

export const EVM_RPC: Partial<Record<ChainKey, string>> = {
  base: "https://mainnet.base.org",
  arbitrum: "https://arb1.arbitrum.io/rpc",
  ethereum: "https://ethereum-rpc.publicnode.com",
  bsc: "https://bsc-dataseed.bnbchain.org",
  robinhood: "https://rpc.mainnet.chain.robinhood.com",
  hyperevm: "https://rpc.hyperliquid.xyz/evm",
};
export const SUI_RPC = "https://fullnode.mainnet.sui.io";

export type BalanceRpcs = { solanaRpc?: string; jupnetRpc?: string };

async function rpc(url: string, method: string, params: unknown[], f: typeof fetch): Promise<any> {
  const r = await f(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }) });
  if (!r.ok) throw new Error(`${method}: HTTP ${r.status}`);
  const j = (await r.json()) as any;
  if (j?.error) throw new Error(`${method}: ${j.error.message ?? JSON.stringify(j.error)}`);
  return j.result;
}

/** `balanceOf(owner)` calldata. */
export const balanceOfData = (owner: string) => `0x70a08231${owner.replace(/^0x/, "").toLowerCase().padStart(64, "0")}`;

/** The sum of an owner's token accounts of `mint` (SPL, Token-2022 or Jupnet's JPL). */
async function splBalance(url: string, owner: string, mint: string, f: typeof fetch): Promise<bigint> {
  const res = await rpc(url, "getTokenAccountsByOwner", [owner, { mint }, { encoding: "jsonParsed" }], f);
  return (res?.value ?? []).reduce((n: bigint, a: any) => n + BigInt(a?.account?.data?.parsed?.info?.tokenAmount?.amount ?? "0"), 0n);
}

/** An EVM token's symbol and decimals, read off the contract (a custom token's fields filled in). */
export async function evmTokenMeta(chain: Chain, addr: string, f: typeof fetch = fetch): Promise<{ sym: string; decimals: number } | null> {
  const url = EVM_RPC[chain.key];
  if (!url) return null;
  try {
    const [d, s] = await Promise.all([rpc(url, "eth_call", [{ to: addr, data: "0x313ce567" }, "latest"], f), rpc(url, "eth_call", [{ to: addr, data: "0x95d89b41" }, "latest"], f)]);
    const decimals = Number(BigInt(d));
    if (!Number.isInteger(decimals) || decimals > 36) return null;
    return { sym: decodeAbiString(s) || "TOKEN", decimals };
  } catch {
    return null;
  }
}

/** An ABI-encoded `string` return (or the bytes32 some old tokens give), as text. */
export function decodeAbiString(hex: string): string {
  const h = hex.replace(/^0x/, "");
  const text = (x: string) => new TextDecoder().decode(Uint8Array.from(x.match(/../g) ?? [], (b) => parseInt(b, 16))).replace(/\0+$/, "");
  if (h.length === 64) return text(h);
  if (h.length < 128) return "";
  const len = Number(BigInt(`0x${h.slice(64, 128)}`));
  return text(h.slice(128, 128 + len * 2)).slice(0, 16);
}

/** `owner`'s balance of `token` on `chain` in smallest units, or null when it cannot be read here. */
export async function readBalance(chain: Chain, token: Token, owner: string, rpcs: BalanceRpcs = {}, f: typeof fetch = fetch): Promise<bigint | null> {
  if (!owner || token.decimals < 0 || !token.addr) return null;
  try {
    switch (chain.family) {
      case "evm": {
        const url = EVM_RPC[chain.key];
        if (!url) return null;
        if (isNativeEvm(token.addr)) return BigInt(await rpc(url, "eth_getBalance", [owner, "latest"], f));
        const out = await rpc(url, "eth_call", [{ to: token.addr, data: balanceOfData(owner) }, "latest"], f);
        return out && out !== "0x" ? BigInt(out) : 0n;
      }
      case "solana": {
        if (!rpcs.solanaRpc) return null;
        if (token.addr === NATIVE_SOL_MINT) return BigInt((await rpc(rpcs.solanaRpc, "getBalance", [owner, { commitment: "confirmed" }], f))?.value ?? 0);
        return await splBalance(rpcs.solanaRpc, owner, token.addr, f);
      }
      case "jupnet":
        return rpcs.jupnetRpc ? await splBalance(rpcs.jupnetRpc, owner, token.addr, f) : null;
      case "sui":
        return BigInt((await rpc(SUI_RPC, "suix_getBalance", [owner, token.addr], f))?.totalBalance ?? "0");
    }
  } catch {
    return null;
  }
}
